import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Menu } from 'primereact/menu';
import { MenuItem, MenuItemOptions } from 'primereact/menuitem';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ImportData from '../../../../assets/icons/ImportData';
import { FileImportType, useImportFile } from '../../../../hooks/api/imports';
import {
  ExportProductsResponse,
  useExportProducts,
  useUpdatePcfValues,
} from '../../../../hooks/api/products';
import useDialogContext from '../../../../hooks/dialog/useDialogContext';
import useNotificationContext from '../../../../hooks/notification/useNotificationContext';
import { InternalEvent } from '../../../../layouts/app/socket-message-handlers';
import { ToastSeverity } from '../../../../shared/enums/toast-severity';
import styles from './ProductActionsButton.module.css';

const ProductActionsButton = () => {
  const { t } = useTranslation();
  const { notify } = useNotificationContext();
  const { openDialog } = useDialogContext();
  const menuRef = useRef<Menu>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [syncedWidth, setSyncedWidth] = useState<number>();

  const queryClient = useQueryClient();

  const [isDisabled, setIsDisabled] = useState(!!sessionStorage.getItem('isImportingFile'));

  // Refs for the hidden file inputs for imports
  const refProductImportInput = useRef<HTMLInputElement | null>(null);
  const refProductAssignmentImportInput = useRef<HTMLInputElement | null>(null);

  // Handler function that will be called when a file import completion event gets caught by this component
  const importFileCompleteEventHandler = () => {
    setIsDisabled(false);
  };

  useEffect(() => {
    // Add event listener for file import completion events to re-enable the split button
    window.addEventListener(InternalEvent.FILE_IMPORT_COMPLETE, importFileCompleteEventHandler);

    // Cleanup those event listener on component unmount
    return () => {
      window.removeEventListener(
        InternalEvent.FILE_IMPORT_COMPLETE,
        importFileCompleteEventHandler,
      );
    };
  }, []);

  const onSuccessExportProducts = (data: ExportProductsResponse) => {
    // If mutation was successful and we have data, then download the file
    const { fileUrl } = data;
    const filename = fileUrl.split('/').pop();

    if (fileUrl && filename) {
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = fileUrl;

      // Set the download attribute if you want to specify the file name
      link.download = filename; // Use the last part of the URL as the file name

      // Append the link to the document body (this step is necessary for some browsers)
      document.body.appendChild(link);

      // Programmatically trigger a click event on the link
      link.click();

      // Clean up by removing the link element
      document.body.removeChild(link);
    }
  };

  const onMutateUpdatePcfValues = () => {
    notify({
      severity: ToastSeverity.INFO,
      detail: t('toastMessages.updatePcfValues.states.updating'),
      life: 5000,
    });
  };

  const onSuccessUpdatePcfValues = () => {
    notify({
      severity: ToastSeverity.SUCCESS,
      detail: t('toastMessages.updatePcfValues.states.success'),
    });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const onErrorUpdatePcfValues = () => {
    notify({
      severity: ToastSeverity.ERROR,
      detail: t('toastMessages.updatePcfValues.states.failure'),
    });
  };

  const { mutate: exportProducts, isLoading: isExportingProducts } = useExportProducts({
    onSuccess: onSuccessExportProducts,
  });

  const { mutate: updatePcfValues } = useUpdatePcfValues({
    onMutate: onMutateUpdatePcfValues,
    onSuccess: onSuccessUpdatePcfValues,
    onError: onErrorUpdatePcfValues,
  });

  const { mutate: importFile } = useImportFile();

  const handleFileImport = (file: File, type: FileImportType) => {
    sessionStorage.setItem('isImportingFile', 'true');
    setIsDisabled(true);
    importFile({ file, type });
  };

  const itemTemplate = (item: MenuItem, options: MenuItemOptions) => {
    return (
      <div className='p-menuitem-content' data-pc-section='content'>
        <a
          className='p-menuitem-link flex'
          aria-label={item.label}
          aria-hidden='true'
          data-pc-section='action'
          onClick={options.onClick}
        >
          <span className='p-menuitem-icon pi' data-pc-section='icon'>
            {typeof item.icon === 'string' ? <span className={item.icon} /> : item.icon}
          </span>
          <span className='p-menuitem-text' data-pc-section='label'>
            {item.label}
          </span>
        </a>
        <Divider className='m-0' />
      </div>
    );
  };

  const items: MenuItem[] = [
    {
      label: t('productsPage.buttons.importData'),
      icon: <ImportData />,
      command: () => {
        openDialog('import-product-pcf-dialog');
      },
      template: itemTemplate,
    },
    {
      label: t('productsPage.buttons.apiImport'),
      icon: 'pi pi-plus',
      command: async () => {
        if (refProductImportInput.current) {
          refProductImportInput.current.click();
        }
      },
    },
    {
      label: t('productsPage.buttons.assign'),
      icon: 'pi pi-user-plus',
      command: async () => {
        if (refProductAssignmentImportInput.current) {
          refProductAssignmentImportInput.current.click();
        }
      },
    },
    {
      label: t('productsPage.buttons.export'),
      icon: 'pi pi-download',
      disabled: isExportingProducts,
      command: () => {
        exportProducts();
      },
    },
    {
      label: t('productsPage.buttons.update'),
      icon: 'pi pi-sync',
      command: () => {
        openDialog('confirmation-dialog', {
          title: t('updatePcfValuesDialog.title'),
          message: t('updatePcfValuesDialog.content'),
          confirmButtonText: t('updatePcfValuesDialog.buttons.yesUpdate'),
          cancelButtonText: t('updatePcfValuesDialog.buttons.noKeepCurrent'),
          onConfirm: updatePcfValues,
        });
      },
    },
  ];

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    menuRef?.current?.toggle(event);
  };

  /**
   * Calculates a single width that will be shared by both
   * the Button and the popup Menu
   */
  const syncWidths = () => {
    const buttonWidth = buttonRef.current?.offsetWidth ?? 0;
    requestAnimationFrame(() => {
      const menuRoot = menuRef.current?.getElement();
      const menuWidth = menuRoot?.getBoundingClientRect().width ?? 0;

      setSyncedWidth(Math.max(buttonWidth, menuWidth));
    });
  };

  return (
    <div className='flex flex-column md:flex-row gap-2 md:gap-2'>
      <Menu
        ref={menuRef}
        popup
        model={items}
        onShow={syncWidths}
        style={{ width: syncedWidth ? `${syncedWidth}px` : 'auto' }}
      />

      <Button
        label={t('productsPage.buttons.bulkActions')}
        icon='pi pi-plus-circle'
        onClick={onClick}
        className={`${styles['bulk-actions']} w-full md:w-fit flex justify-content-center`}
        disabled={isDisabled}
        loading={isDisabled}
        style={{ width: syncedWidth ? `${syncedWidth}px` : 'auto' }}
        pt={{
          root: {
            ref: buttonRef,
          },
        }}
      />

      {/* Hidden inputs for file selections */}
      <input
        id='product-assignment-import-input'
        type='file'
        ref={refProductAssignmentImportInput}
        accept='.xlsx,.xls'
        multiple={false}
        onChange={(e) => {
          if (e.target.files?.[0]) {
            handleFileImport(e.target.files[0], FileImportType.ASSIGN_PRODUCTS);
          }
        }}
        hidden
        value=''
      />

      <input
        id='product-import-input'
        type='file'
        ref={refProductImportInput}
        accept='.xlsx,.xls'
        multiple={false}
        onChange={(e) => {
          if (e.target.files?.[0]) {
            handleFileImport(e.target.files[0], FileImportType.IMPORT_PRODUCTS);
          }
        }}
        hidden
        value=''
      />

      <div className='flex flex-row align-items-center gap-1 justify-content-between md:justify-content-center w-full md:w-fit'>
        <span>{t('productsPage.buttons.templates')}</span>

        <div className='flex flex-row gap-1 align-items-center'>
          <a
            href={
              process.env.REACT_APP_S3_CONTENT_URL + '/excel-templates/PACIFIC_Template_Import.xlsx'
            }
            download='PACIFIC_Template_Import.xlsx'
            target='_blank'
            rel='noreferrer'
            className='text-primary font-medium'
          >
            {t('productsPage.buttons.import')}
          </a>
          <span>|</span>
          <a
            href={
              process.env.REACT_APP_S3_CONTENT_URL + '/excel-templates/PACIFIC_Template_Assign.xlsx'
            }
            download='PACIFIC_Template_Assign.xlsx'
            target='_blank'
            rel='noreferrer'
            className='text-primary font-medium'
          >
            {t('productsPage.buttons.assign')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProductActionsButton;
